import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2, Plus } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH, cn } from "@/lib/utils";

import {
  FIRMWARE_LIST_KEY,
  FIRMWARE_WORKFLOW_STATES,
  fetchFirmwareList,
  fetchFirmwareReleaseNoteDetail,
  fetchFirmwareSerialDeviceCounts,
  firmwareReleaseNoteDetailKey,
  firmwareReleaseNoteProductLineLabel,
  firmwareRevisionEffectiveDisplay,
  firmwareRevisionEndingDisplay,
  firmwareRecordFileName,
  firmwareWorkflowTone,
  FIRMWARE_SERIAL_DEVICE_COUNTS_KEY,
  FIRMWARE_FILE_SERIAL_DEVICE_COUNT_HOVER,
  type FirmwareRecord,
  type FirmwareWorkflowState,
} from "./firmwareApi";
import { FirmwareComposeModal, type FirmwareComposeMode } from "./FirmwareComposeModal";
import { FirmwareDeferredDeviceCount } from "./FirmwareDeferredDeviceCount";
import { FirmwareFamilyChips } from "./FirmwareFamilyChips";
import { FirmwareFeatureChips } from "./FirmwareFeatureChips";
import { FirmwareReleaseNoteMarkdown } from "./FirmwareReleaseNoteMarkdown";
import { FirmwareReleaseNotesSplit } from "./FirmwareReleaseNotesSplit";
import { parseReleaseNoteFilterId } from "./firmwareReleaseNoteSelection";

const HELP_TEXT = [
  "Firmware files live under Files. Shared changelogs live under Release notes,",
  "so one 6.67 note can cover several hardware-specific hex files.",
].join(" ");

type FirmwareTab = "files" | "notes";
type ReleaseSortKey =
  | "fileName"
  | "noteVersion"
  | "productLine"
  | "pcbaFamily"
  | "feature"
  | "revisionEffective"
  | "revisionEnding"
  | "serialDeviceCount"
  | "releasedOn"
  | "workflowState";

function parseTab(raw: string | null): FirmwareTab {
  return raw === "notes" ? "notes" : "files";
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

export function FirmwareListPage(): JSX.Element {
  const writesAllowed = useHubModuleWrite("engineering");
  const { showToast } = useHubToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("tab"));
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState<FirmwareComposeMode>("both");
  const [presetReleaseNoteId, setPresetReleaseNoteId] = useState<number | null>(null);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    setComposeMode(searchParams.get("tab") === "notes" ? "notes" : "both");
    setPresetReleaseNoteId(null);
    setComposeOpen(true);
    const params = new URLSearchParams(searchParams);
    params.delete("new");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!newMenuOpen) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (newMenuRef.current?.contains(event.target as Node)) return;
      setNewMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setNewMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [newMenuOpen]);

  const openCompose = (mode: FirmwareComposeMode, releaseNoteId: number | null = null) => {
    setNewMenuOpen(false);
    setComposeMode(mode);
    setPresetReleaseNoteId(releaseNoteId);
    setComposeOpen(true);
  };

  const setTab = (next: FirmwareTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === "files") params.delete("tab");
    else {
      params.set("tab", "notes");
      params.delete("releaseNoteId");
    }
    setSearchParams(params, { replace: true });
  };

  const openReleasesForNote = (releaseNoteId: number) => {
    const params = new URLSearchParams(searchParams);
    params.delete("tab");
    params.delete("ids");
    params.set("releaseNoteId", String(releaseNoteId));
    setSearchParams(params);
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT}>Firmware</PageTitleWithHelp>
        {writesAllowed ? (
          <div ref={newMenuRef} className="relative">
            <Button
              type="button"
              aria-haspopup="menu"
              aria-expanded={newMenuOpen}
              onClick={() => setNewMenuOpen((open) => !open)}
            >
              <Plus className="mr-2 h-4 w-4" />
              New
              <ChevronDown className="ml-2 h-4 w-4" aria-hidden />
            </Button>
            {newMenuOpen ? (
              <ul
                role="menu"
                className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-md border border-slate-200 bg-white py-1 text-sm text-slate-800 shadow-lg"
              >
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => openCompose("firmware")}
                  >
                    Firmware file
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => openCompose("notes")}
                  >
                    Release notes
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => openCompose("both")}
                  >
                    Both
                  </button>
                </li>
              </ul>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant={tab === "files" ? "primary" : "secondary"} onClick={() => setTab("files")}>
          Files
        </Button>
        <Button type="button" size="sm" variant={tab === "notes" ? "primary" : "secondary"} onClick={() => setTab("notes")}>
          Release notes
        </Button>
      </div>

      {tab === "notes" ? (
        <FirmwareReleaseNotesSplit
          writesAllowed={writesAllowed}
          onOpenReleasesForNote={openReleasesForNote}
          onAddFirmwareFile={(releaseNoteId) => openCompose("firmware", releaseNoteId)}
        />
      ) : (
        <ReleasesPanel />
      )}

      <FirmwareComposeModal
        open={composeOpen}
        mode={composeMode}
        presetReleaseNoteId={presetReleaseNoteId}
        onClose={() => {
          setComposeOpen(false);
          setPresetReleaseNoteId(null);
        }}
        onCreated={(created) => {
          if (created.releaseNoteId != null && created.firmwareId != null) {
            showToast({ tone: "success", message: "Created release notes and firmware." });
          } else if (created.releaseNoteId != null) {
            showToast({ tone: "success", message: "Created release notes." });
          } else if (created.firmwareId != null) {
            showToast({ tone: "success", message: "Created firmware file." });
          }
          const params = new URLSearchParams(searchParams);
          if (created.firmwareId != null && created.releaseNoteId != null && composeMode === "firmware") {
            params.delete("tab");
            params.delete("ids");
            params.set("releaseNoteId", String(created.releaseNoteId));
          } else if (created.releaseNoteId != null) {
            params.set("tab", "notes");
            params.set("ids", String(created.releaseNoteId));
            params.delete("releaseNoteId");
          } else {
            params.delete("tab");
            params.delete("ids");
            params.delete("releaseNoteId");
          }
          setSearchParams(params, { replace: true });
        }}
      />
    </div>
  );
}

function ReleasesPanel(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const releaseNoteId = parseReleaseNoteFilterId(searchParams.get("releaseNoteId"));
  const [search, setSearch] = useState("");
  const [workflowFilter, setWorkflowFilter] = useState<FirmwareWorkflowState | "all">("all");
  const [sortKey, setSortKey] = useState<ReleaseSortKey>("releasedOn");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const listQuery = useQuery({
    queryKey: [...FIRMWARE_LIST_KEY, search, workflowFilter, releaseNoteId],
    queryFn: () =>
      fetchFirmwareList({
        q: search.trim() || undefined,
        workflowState: workflowFilter,
        releaseNoteId,
        limit: 200,
      }),
  });

  const countsQuery = useQuery({
    queryKey: FIRMWARE_SERIAL_DEVICE_COUNTS_KEY,
    queryFn: fetchFirmwareSerialDeviceCounts,
    staleTime: 60_000,
  });
  const countsReady = countsQuery.isSuccess;
  const countByFirmwareId = useMemo(() => {
    const map = new Map<number, number>();
    for (const row of countsQuery.data?.counts ?? []) {
      map.set(row.firmwareId, row.deviceCount);
    }
    return map;
  }, [countsQuery.data?.counts]);
  const skuByFirmwareId = useMemo(() => {
    const map = new Map<number, FirmwareRecord["serialDeviceCountBySku"]>();
    for (const row of countsQuery.data?.counts ?? []) {
      map.set(row.firmwareId, row.deviceCountBySku ?? []);
    }
    return map;
  }, [countsQuery.data?.counts]);

  const noteFilterQuery = useQuery({
    queryKey: firmwareReleaseNoteDetailKey(releaseNoteId ?? 0),
    queryFn: () => fetchFirmwareReleaseNoteDetail(releaseNoteId ?? 0),
    enabled: releaseNoteId != null,
  });

  const rows = useMemo(() => {
    const list = (listQuery.data?.firmware ?? []).map((row) => ({
      ...row,
      serialDeviceCount: countsReady ? (countByFirmwareId.get(row.id) ?? 0) : null,
      serialDeviceCountBySku: countsReady ? (skuByFirmwareId.get(row.id) ?? []) : [],
    }));
    list.sort((left, right) => {
      const sign = sortDir === "asc" ? 1 : -1;
      if (sortKey === "releasedOn") {
        return sign * compareText(left.releasedOn ?? "", right.releasedOn ?? "");
      }
      if (sortKey === "workflowState") return sign * compareText(left.workflowState, right.workflowState);
      if (sortKey === "pcbaFamily") {
        return sign * compareText(left.pcbaFamily ?? "", right.pcbaFamily ?? "");
      }
      if (sortKey === "feature") return sign * compareText(left.feature ?? "", right.feature ?? "");
      if (sortKey === "revisionEffective") {
        return sign * compareText(firmwareRevisionEffectiveDisplay(left), firmwareRevisionEffectiveDisplay(right));
      }
      if (sortKey === "revisionEnding") {
        return sign * compareText(firmwareRevisionEndingDisplay(left), firmwareRevisionEndingDisplay(right));
      }
      if (sortKey === "serialDeviceCount") {
        return sign * ((left.serialDeviceCount ?? 0) - (right.serialDeviceCount ?? 0));
      }
      if (sortKey === "fileName") {
        return sign * compareText(firmwareRecordFileName(left), firmwareRecordFileName(right));
      }
      if (sortKey === "noteVersion") {
        return sign * compareText(left.releaseNoteVersionKey ?? "", right.releaseNoteVersionKey ?? "");
      }
      if (sortKey === "productLine") {
        return (
          sign *
          compareText(
            left.releaseNoteProductLine ? firmwareReleaseNoteProductLineLabel(left.releaseNoteProductLine) : "",
            right.releaseNoteProductLine ? firmwareReleaseNoteProductLineLabel(right.releaseNoteProductLine) : "",
          )
        );
      }
      return sign * compareText(left.version, right.version);
    });
    return list;
  }, [countByFirmwareId, countsReady, listQuery.data?.firmware, skuByFirmwareId, sortDir, sortKey]);

  const onSort = (key: ReleaseSortKey) => {
    if (sortKey === key) setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "releasedOn" ? "desc" : "asc");
    }
  };

  const selectClassName = cn(
    "h-10 rounded-md border border-slate-300 bg-white px-3 text-sm",
    "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
  );

  const columns: HubDataTableColumn<FirmwareRecord, ReleaseSortKey>[] = useMemo(
    () => [
      {
        id: "fileName",
        header: "File name",
        sortKey: "fileName",
        headerClassName: "w-[28%]",
        sortValue: (row) => firmwareRecordFileName(row),
        cell: (row) => (
          <Link className="font-medium text-sky-700 hover:underline" to={`/engineering/firmware/${row.id}`}>
            {firmwareRecordFileName(row) || EM_DASH}
          </Link>
        ),
      },
      {
        id: "noteVersion",
        header: "Version",
        sortKey: "noteVersion",
        overflow: "nowrap",
        sortValue: (row) => row.releaseNoteVersionKey ?? "",
        cell: (row) =>
          row.releaseNoteId != null && row.releaseNoteVersionKey != null ? (
            <HoverHintTrigger
              align="start"
              closeOnTriggerActivate
              trigger={
                <Link
                  className="font-medium text-sky-700 hover:underline"
                  to={`/engineering/firmware?tab=notes&ids=${String(row.releaseNoteId)}`}
                >
                  {row.releaseNoteVersionKey}
                </Link>
              }
            >
              <FirmwareReleaseNoteMarkdown compact className="max-w-md" text={row.releaseNoteNotes ?? ""} />
            </HoverHintTrigger>
          ) : (
            EM_DASH
          ),
      },
      {
        id: "pcbaFamily",
        header: "Family",
        sortKey: "pcbaFamily",
        overflow: "nowrap",
        sortValue: (row) => row.pcbaFamily ?? "",
        cell: (row) => <FirmwareFamilyChips family={row.pcbaFamily} />,
      },
      {
        id: "feature",
        header: "Feature",
        sortKey: "feature",
        overflow: "nowrap",
        cellClassName: "min-w-[7rem]",
        sortValue: (row) => row.feature ?? "",
        cell: (row) => <FirmwareFeatureChips feature={row.feature} />,
      },
      {
        id: "revisionEffective",
        header: "Effective",
        sortKey: "revisionEffective",
        overflow: "nowrap",
        sortValue: (row) => firmwareRevisionEffectiveDisplay(row),
        cell: (row) => firmwareRevisionEffectiveDisplay(row) || EM_DASH,
      },
      {
        id: "revisionEnding",
        header: "Ending",
        sortKey: "revisionEnding",
        overflow: "nowrap",
        sortValue: (row) => firmwareRevisionEndingDisplay(row),
        cell: (row) => firmwareRevisionEndingDisplay(row) || EM_DASH,
      },
      {
        id: "serialDeviceCount",
        header: "Devices",
        sortKey: "serialDeviceCount",
        align: "right",
        overflow: "nowrap",
        sortValue: (row) => row.serialDeviceCount ?? 0,
        cell: (row) => (
          <FirmwareDeferredDeviceCount
            count={row.serialDeviceCount}
            ready={countsReady}
            hoverHint={FIRMWARE_FILE_SERIAL_DEVICE_COUNT_HOVER}
            bySku={row.serialDeviceCountBySku ?? []}
          />
        ),
      },
      {
        id: "productLine",
        header: "Product line",
        sortKey: "productLine",
        overflow: "nowrap",
        sortValue: (row) =>
          row.releaseNoteProductLine ? firmwareReleaseNoteProductLineLabel(row.releaseNoteProductLine) : "",
        cell: (row) =>
          row.releaseNoteProductLine
            ? firmwareReleaseNoteProductLineLabel(row.releaseNoteProductLine)
            : EM_DASH,
      },
      {
        id: "workflowState",
        header: "State",
        sortKey: "workflowState",
        overflow: "nowrap",
        sortValue: (row) => row.workflowState,
        cell: (row) => <Badge tone={firmwareWorkflowTone(row.workflowState)}>{row.workflowState}</Badge>,
      },
      {
        id: "releasedOn",
        header: "Released",
        sortKey: "releasedOn",
        overflow: "nowrap",
        sortValue: (row) => row.releasedOn ?? "",
        cell: (row) => row.releasedOn ?? EM_DASH,
      },
    ],
    [countsReady],
  );

  const filtersActive = search.trim() !== "" || workflowFilter !== "all" || releaseNoteId != null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor="firmware-search">Search files</Label>
            <Input
              id="firmware-search"
              type="search"
              className="mt-1.5"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by file name, family, or feature"
            />
          </div>
          <div>
            <Label htmlFor="firmware-state">Workflow</Label>
            <select
              id="firmware-state"
              className={cn(selectClassName, "mt-1.5")}
              value={workflowFilter}
              onChange={(event) => setWorkflowFilter(event.target.value as FirmwareWorkflowState | "all")}
            >
              <option value="all">All states</option>
              {FIRMWARE_WORKFLOW_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          {releaseNoteId != null ? (
            <div className="md:col-span-2 flex flex-wrap items-center gap-2 text-sm text-slate-700">
              <span>
                Showing firmware linked to release notes{" "}
                {noteFilterQuery.data?.releaseNote.versionKey ?? `#${releaseNoteId}`}.
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.delete("releaseNoteId");
                  setSearchParams(params, { replace: true });
                }}
              >
                Clear release-notes filter
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <HubDataTable
        title="Files"
        headerExtra={
          rows.length > 0 ? (
            <p className="text-xs font-normal text-slate-500">
              Showing {rows.length} of {listQuery.data?.total ?? rows.length}
            </p>
          ) : null
        }
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        loading={listQuery.isLoading}
        loadingMessage={
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading firmware…
          </span>
        }
        error={listQuery.isError ? (listQuery.error as Error).message : null}
        empty="No firmware files."
        isFiltered={filtersActive}
        filteredEmpty="No firmware files match these filters."
      />
    </div>
  );
}
